import React, { useState, useEffect } from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import { activateAuthLayout } from "../../../store/actions";
import { connect } from "react-redux";
import { gql } from "apollo-boost";
import { useQuery, useMutation } from "@apollo/react-hooks";
import Creatable from "react-select/creatable";
import SweetAlert from "react-bootstrap-sweetalert";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { getQuoteBuilderURL } from "../../../helpers/common";
import ColumnHeadersTooltip from "../../../components/ColumnHeadersTooltip";
import { titleCase } from "../../../helpers/textTransform";
import { useHistory } from 'react-router';

const DATA = gql`
  query Data($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      Description
      PrimaryContact
      QuoteTypeId1
      QuoteType1 {
        QuoteTypeId
        Name
      }
      QuoteTypeId2
      QuoteType2 {
        QuoteTypeId
        Name
      }
      WorkflowStep
      AcceptedQuote
      Status
      IsTemplate
    }

    QuoteRates(QuoteId: $QuoteId) {
      QuoteRateId
      QuoteId
      RateType
      RateUnits
      Quote1Rate
      Quote2Rate
      CompareDesc
      ComparePrice
      CompareQty
      Order
    }
  }
`;

const CREATE_QUOTE_RATES = gql`
  mutation createQuoteRates($QuoteRatesList: [QuoteRatesList]) {
    createQuoteRates(input: $QuoteRatesList) {
      QuoteRateId
      QuoteId
      RateType
      RateUnits
      Quote1Rate
      Quote2Rate
      CompareDesc
      ComparePrice
      CompareQty
      Order
    }
  }
`;

const UPDATE_QUOTE_RATE = gql`
  mutation updateQuoteRate($QuoteRateInput: QuoteRateInput) {
    updateQuoteRate(input: $QuoteRateInput) {
      QuoteRateId
      QuoteId
      RateType
      RateUnits
      Quote1Rate
      Quote2Rate
      CompareDesc
      ComparePrice
      CompareQty
      Order
    }
  }
`;

const UPDATE_QUOTE_RATES = gql`
  mutation updateQuoteRates($QuoteRatesList: [QuoteRatesList]) {
    updateQuoteRates(input: $QuoteRatesList) {
      QuoteRateId
      QuoteId
      RateType
      RateUnits
      Quote1Rate
      Quote2Rate
      CompareDesc
      ComparePrice
      CompareQty
      Order
    }
  }
`;

const DELETE_QUOTE_RATE = gql`
  mutation deleteQuoteRate($QuoteRateId: ID!) {
    deleteQuoteRate(QuoteRateId: $QuoteRateId) {
      QuoteRateId
      QuoteId
      RateType
      RateUnits
      Quote1Rate
      Quote2Rate
      CompareDesc
      ComparePrice
      CompareQty
      Order
    }
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      CompanyName
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
    }
  }
`;

const QUOTE_RATES_COLUMN_HEADERS = [
  {
    id: 1,
    field: 'RateType',
    label: 'Rate Type',
    description: 'The type of usage rate for this line item',
    minWidth: '250px',
  },
  {
    id: 2,
    field: 'Quote1Rate',
    label: 'Quote 1 Price',
    description: 'The price for this rate type for the first quote option',
    width: '15%',
  },
  {
    id: 3,
    field: 'Quote2Rate',
    label: 'Quote 2 Price',
    description: 'The price for this rate type for the second quote option',
    width: '15%',
  },
  {
    id: 4,
    field: 'RateUnits',
    label: 'Rate Unit',
    description: 'The increment or unit type for this rate (eg. "Flat" or "Per minute")',
    width: '15%',
  },
  {
    id: 5,
    field: 'CompareDesc',
    label: 'Comp Line Item',
    description: "The line item on a competitor's quote or bill to which this rate should be compared",
    width: '15%',
  },
  {
    id: 6,
    field: 'ComparePrice',
    label: 'Comp Price',
    description: "The competitor's prices for the comparison line item",
    width: '15%',
  },
  {
    id: 7,
    field: 'CompareQty',
    label: 'Comp Quantity',
    description: "The rate unit quantity from the competitor's quote or bill for comparison purposes",
    width: '15%',
  },
];

const QuoteBuilderRates = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const history = useHistory();

  // initialize states
  const [stepError, setStepError] = useState(null);
  const [quoteRates, setQuoteRates] = useState(null);
  const [newQuoteRate, setNewQuoteRate] = useState({});
  const [disableQuote, setDisableQuote] = useState(false);

  const [RATE_TYPES, SET_RATE_TYPES] = useState([
    { label: "Local Calls", value: "LOCAL_CALLS" },
    { label: "National Calls", value: "NATIONAL_CALLS" },
    { label: "Mobile Calls", value: "MOBILE_CALLS" },
    { label: "1300 Calls", value: "_1300_CALLS" },
    { label: "1800 Calls", value: "_1800_CALLS" },
    { label: "Mobile Data Overage", value: "MOBILE_DATA_OVERAGE" },
    { label: "Data Overage", value: "DATA_OVERAGE" },
  ]);

  const [rateTypeValue, setRateTypeValue] = useState();


  const createOption = (v) => ({
    label: titleCase(v),
    value: v
      .replace(" ", "_")
      .toUpperCase(),
  });

  // initialize apollo graphql queries and mutations
  const { data, loading } = useQuery(DATA, {
    variables: { QuoteId: props.quoteId },
  });
  const isTemplate = !!data?.Quote?.IsTemplate;
  const [createQuoteRates, { data: createdQuoteRates }] =
    useMutation(CREATE_QUOTE_RATES);
  const [updateQuoteRate] = useMutation(UPDATE_QUOTE_RATE);
  const [updateQuoteRates, { data: updatedQuoteRates }] =
    useMutation(UPDATE_QUOTE_RATES);
  const [deleteQuoteRate, { data: deletedQuoteRate }] =
    useMutation(DELETE_QUOTE_RATE);
  const [updateQuote] = useMutation(UPDATE_QUOTE);

  useEffect(() => {
    if (data && data.Quote && data.QuoteRates) {
      // disable screen when status is accepted, declined or voided
      const status = data.Quote.Status;
      setDisableQuote(status === 'accepted');

      if (!['QUOTE_RATES', 'CUSTOMER_ACCEPTED'].includes(data.Quote.WorkflowStep)) {
        history.replace(getQuoteBuilderURL(data.Quote.WorkflowStep, data.Quote.QuoteId));
        return;
      }

      setQuoteRates(data.QuoteRates);
    }
  }, [data, history]);

  useEffect(() => {
    if (data && createdQuoteRates) {
      if (createdQuoteRates.createQuoteRates.length > 0) {
        setQuoteRates(prev => [...prev, createdQuoteRates.createQuoteRates[0]]);
      } else {
        setStepError({
          error: true,
          message: "Rate type already exists.",
        });
      }
    }
  }, [createdQuoteRates, data]);

  useEffect(() => {
    if (data && updatedQuoteRates) {
      setQuoteRates(updatedQuoteRates.updateQuoteRates);
    }
  }, [updatedQuoteRates, data]);

  useEffect(() => {
    if (data && deletedQuoteRate) {
      const deletedId = deletedQuoteRate.deleteQuoteRate.QuoteRateId;
      setQuoteRates(prev => prev.filter(rate => rate.QuoteRateId !== deletedId));
    }
  }, [deletedQuoteRate, data]);

  const onNewQuoteRateChange = (e, field) => {
    const qr = { ...newQuoteRate };
    qr[field] = e.target ? e.target.value : e.value;
    setNewQuoteRate(qr);
  };

  const addNewQuoteRate = () => {
    if (
      newQuoteRate.RateType !== "" &&
      quoteRates.filter((rate) => rate.RateType === newQuoteRate.RateType)
        .length > 0
    ) {
      setStepError({
        error: true,
        message: "Rate type already exists.",
      });
    } else if (
      Object.keys(newQuoteRate).length > 0 &&
      newQuoteRate.RateType !== ""
    ) {
      newQuoteRate.Order =
        quoteRates.length > 0 ? quoteRates[quoteRates.length - 1].Order + 1 : 1;
      newQuoteRate.QuoteId = props.quoteId;
      createQuoteRates({
        variables: {
          // QuoteRatesList: [newQuoteRate],
          QuoteRatesList: [{
            ...newQuoteRate,
            QuoteId: parseInt(newQuoteRate.QuoteId),
            Quote1Rate: parseFloat(newQuoteRate.Quote1Rate),
            Quote2Rate: parseFloat(newQuoteRate.Quote2Rate),
            ComparePrice: parseFloat(newQuoteRate.ComparePrice),
            CompareQty: parseInt(newQuoteRate.CompareQty),
          }],
        },
        refetchQueries: ['getCurrentQuote'],
      });
      setNewQuoteRate({});
      setRateTypeValue(null);
    } else {
      setStepError({
        error: true,
        message:
          "Rate Type is required.",
      });
    }
  };

  /**
   * Update the quoteRate state when user update the fields of the quote rates
   */
  const updateQuoteRateEntry = (e, field, quoteRateId) => {
    const index = quoteRates
      .map((rate) => rate.QuoteRateId)
      .indexOf(quoteRateId);
    const newItem = Object.assign({}, quoteRates[index]);
    const rawValue = e.target ? e.target.value : e.value;
    switch (field) {
      case 'Quote1Rate':
      case 'Quote2Rate':
      case 'ComparePrice':
        newItem[field] = parseFloat(rawValue);
        break;
      case 'QuoteId':
      case 'CompareQty':
      case 'Order':
        newItem[field] = parseInt(rawValue);
        break;
      default:
        newItem[field] = rawValue;
    }
    const newArr = [...quoteRates];
    newArr[index] = newItem;
    updateQuoteRate({
      variables: {
        QuoteRateInput: newItem,
      },
      refetchQueries: ['getCurrentQuote'],
    });
    setQuoteRates(newArr);
  };

  /**
   * delete the quote rate entry, also in the DB
   */
  const deleteQuoteRateEntry = (e, quoteRateId) => {
    e.preventDefault();
    deleteQuoteRate({
      variables: {
        QuoteRateId: quoteRateId,
      },
      refetchQueries: ['getCurrentQuote'],
    });
  };

  const goToPreviousStep = (e) => {
    e.preventDefault();
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: "QUOTE_ITEMS",
        },
      },
    }).then(() => {
      history.replace(`/quote-items/${data.Quote.QuoteId}`);
    });
  };

  const proceedNextStep = (e) => {
    e.preventDefault();
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: "CUSTOMISE_PROPOSAL",
        },
      },
    }).then(() => {
      history.replace(`/customise-proposal/${data.Quote.QuoteId}`);
    });
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination || source.index === destination.index) {
      return;
    }

    const items = [...quoteRates];
    const [removed] = items.splice(source.index, 1);
    items.splice(destination.index, 0, removed);
    const reordered = items.map((rate, i) => ({ ...rate, Order: i }));

    setQuoteRates(reordered);
    updateQuoteRates({
      variables: {
        QuoteRatesList: reordered,
      },
      refetchQueries: ['getCurrentQuote'],
    });
  };

  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: "none",
    background: isDragging ? "#f0f4f7" : "#fff",
    ...draggableStyle,
  });

  return (
    <React.Fragment>
      {disableQuote && <div className="overlay"></div>}
      {stepError && stepError.error === true && (
        <SweetAlert
          error
          title="Error"
          onConfirm={(e) => setStepError({ error: false, message: "" })}
        >
          {stepError.message}
        </SweetAlert>
      )}
      {
        // load live search and quote item builder UI when quote with the ID passed on the URL exists
        data && data.Quote && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">{data.Quote.Description} - Quote Rates</h4>
                  </Col>
                </Row>
              </div>
              <h6>USAGE RATES</h6>
              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId={"quote-rates"}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              className="table-responsive"
                              key={"quote-rates"}
                            >
                              {data && data.Quote && (
                                <table className="table mb-0">
                                  <thead className="table-dark">
                                    <tr className="text-nowrap">
                                      {QUOTE_RATES_COLUMN_HEADERS.map(
                                        (column) => (
                                          <ColumnHeadersTooltip
                                            key={column.id}
                                            id={"product-pricing"}
                                            name={"usage-rates"}
                                            column={column}
                                            quoteType1={
                                              data.Quote.QuoteType1?.Name
                                            }
                                            quoteType2={
                                              data.Quote.QuoteType2?.Name
                                            }
                                          />
                                        )
                                      )}
                                      <th style={{ minWidth: '100px' }}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {data.Quote && quoteRates?.length ? (
                                      quoteRates.map((item, index) => (
                                        <Draggable
                                          key={item.QuoteRateId}
                                          draggableId={item.QuoteRateId}
                                          index={index}
                                        >
                                          {(provided, snapshot) => (
                                            <tr
                                              key={item.QuoteRateId}
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              style={getItemStyle(
                                                snapshot.isDragging,
                                                provided.draggableProps.style
                                              )}
                                            >
                                              <td>
                                                <i
                                                  className="fas fa-grip-vertical mr-2"
                                                  style={{ color: "#bbb" }}
                                                  {...provided.dragHandleProps}
                                                ></i>
                                                {titleCase(item.RateType)}
                                              </td>
                                              <td>
                                                <input
                                                  type="number"
                                                  value={item.Quote1Rate ?? ''}
                                                  onChange={(e) =>
                                                    updateQuoteRateEntry(
                                                      e,
                                                      "Quote1Rate",
                                                      item.QuoteRateId
                                                    )
                                                  }
                                                  className="form-control"
                                                  readOnly={isTemplate}
                                                  disabled={!data.Quote.QuoteTypeId1}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="number"
                                                  value={item.Quote2Rate ?? ''}
                                                  onChange={(e) =>
                                                    updateQuoteRateEntry(
                                                      e,
                                                      "Quote2Rate",
                                                      item.QuoteRateId
                                                    )
                                                  }
                                                  className="form-control"
                                                  readOnly={isTemplate}
                                                  disabled={!data.Quote.QuoteTypeId2}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="form-control"
                                                  onChange={(e) => updateQuoteRateEntry(e, "RateUnits", item.QuoteRateId)}
                                                  value={item.RateUnits ?? ''}
                                                  readOnly={isTemplate}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  value={item.CompareDesc ?? ''}
                                                  className="form-control"
                                                  onChange={(e) =>
                                                    updateQuoteRateEntry(
                                                      e,
                                                      "CompareDesc",
                                                      item.QuoteRateId
                                                    )
                                                  }
                                                  readOnly={isTemplate}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="number"
                                                  value={item.ComparePrice ?? ''}
                                                  className="form-control"
                                                  onChange={(e) =>
                                                    updateQuoteRateEntry(
                                                      e,
                                                      "ComparePrice",
                                                      item.QuoteRateId
                                                    )
                                                  }
                                                  readOnly={isTemplate}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="number"
                                                  value={item.CompareQty ?? ''}
                                                  className="form-control"
                                                  onChange={(e) =>
                                                    updateQuoteRateEntry(
                                                      e,
                                                      "CompareQty",
                                                      item.QuoteRateId
                                                    )
                                                  }
                                                  readOnly={isTemplate}
                                                />
                                              </td>
                                              <td align="right">
                                                <button
                                                  className="btn btn-danger"
                                                  onClick={(e) =>
                                                    deleteQuoteRateEntry(
                                                      e,
                                                      item.QuoteRateId
                                                    )
                                                  }
                                                  disabled={isTemplate}
                                                >
                                                  Delete
                                                </button>
                                              </td>
                                            </tr>
                                          )}
                                        </Draggable>
                                      ))
                                    ) : (
                                      <tr key={0}>
                                        <td
                                          colSpan={10}
                                          className="text-center"
                                        >
                                          No quote rates
                                        </td>
                                      </tr>
                                    )}
                                    {provided.placeholder}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>

                      {!isTemplate && (
                        <div className="table-responsive">
                          {data && data.Quote && (
                            <table className="table mb-0">
                              <thead>
                                <tr className="text-nowrap">
                                  {QUOTE_RATES_COLUMN_HEADERS.map((column) => (
                                    <ColumnHeadersTooltip
                                      key={column.id}
                                      id={"product-pricing"}
                                      name={"usage-rates-2"}
                                      column={column}
                                      quoteType1={data.Quote.QuoteType1?.Name}
                                      quoteType2={data.Quote.QuoteType2?.Name}
                                    />
                                  ))}
                                  <th style={{ minWidth: '100px' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr key={0}>
                                  <td style={{ minWidth: '250px' }}>
                                    <div>
                                      <Creatable
                                        placeholder={"Select Usage Rates"}
                                        options={RATE_TYPES.filter(t => !quoteRates?.some(r => r.RateType === t.value))}
                                        onCreateOption={(e) => {
                                          SET_RATE_TYPES((state) => [
                                            ...state,
                                            createOption(e),
                                          ]);
                                          onNewQuoteRateChange(
                                            createOption(e),
                                            "RateType"
                                          );
                                          setRateTypeValue(createOption(e))
                                        }}
                                        defaultValue={null}
                                        value={rateTypeValue}
                                        menuPortalTarget={document.querySelector(
                                          "body"
                                        )}
                                        onChange={(e) => {
                                          onNewQuoteRateChange(e, "RateType")
                                          setRateTypeValue(e)
                                        }
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={newQuoteRate.Quote1Rate ?? ''}
                                      disabled={!data.Quote.QuoteTypeId1}
                                      onChange={(e) =>
                                        onNewQuoteRateChange(e, "Quote1Rate")
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={newQuoteRate.Quote2Rate ?? ''}
                                      disabled={!data.Quote.QuoteTypeId2}
                                      onChange={(e) =>
                                        onNewQuoteRateChange(e, "Quote2Rate")
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={newQuoteRate.RateUnits ?? ''}
                                      onChange={(e) => onNewQuoteRateChange(e, "RateUnits")}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={newQuoteRate.CompareDesc ?? ''}
                                      onChange={(e) =>
                                        onNewQuoteRateChange(e, "CompareDesc")
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={newQuoteRate.ComparePrice ?? ''}
                                      onChange={(e) =>
                                        onNewQuoteRateChange(e, "ComparePrice")
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={newQuoteRate.CompareQty ?? ''}
                                      onChange={(e) =>
                                        onNewQuoteRateChange(e, "CompareQty")
                                      }
                                    />
                                  </td>
                                  <td align="right">
                                    <button
                                      className="btn btn-success"
                                      onClick={(e) => addNewQuoteRate()}
                                    >
                                      Add
                                    </button>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <button
                        className="btn-icon btn btn-light btn-lg float-left"
                        onClick={goToPreviousStep}
                      >
                        {" "}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-back mr-2"></i>
                        </span>{" "}
                        Previous Step
                      </button>
                      <button
                        className="btn-icon btn btn-primary btn-lg float-right"
                        onClick={proceedNextStep}
                      >
                        {" "}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-forward mr-2"></i>
                        </span>{" "}
                        Next Step
                      </button>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
      {
        // when data is loaded and quote id is not valid show quote does not exists error UI
        data && data.Quote === null && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">Error loading this page</h4>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      Quote with id {props.quoteId} does not exist.
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(QuoteBuilderRates);
